<script setup lang="ts">
import { Link, progress, router, usePage } from "@inertiajs/vue3";
import {
  BookUserIcon,
  MessageSquareIcon,
  RefreshCwIcon,
  SettingsIcon,
  UsersIcon,
} from "lucide-vue-next";
import { computed, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { Toaster, toast } from "vue-sonner";

import GlobalNoticeRail, { type GlobalNotice } from "@/components/global-notice-rail.vue";
import NotificationsDropdown from "@/components/notifications-dropdown";
import PwaUpdateDialog from "@/components/pwa-update-dialog";
import SplashScreen from "@/components/splash-screen.vue";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import WelcomeGuide from "@/components/welcome-guide.vue";
import { useNetworkStatus } from "@/composables/use-network-status";
import { useFlash } from "@/hooks/use-flash";
import { useInitials } from "@/hooks/use-initials";
import { useAnnouncementToasts, useUserNotificationToasts } from "@/hooks/use-realtime-events";
import { haptic } from "@/lib/haptics";
import { markWelcomeGuideDismissed, shouldShowWelcomeGuide } from "@/lib/welcome-guide";
import {
  organizationDirectoryPath,
  organizationDiscussionGroupsPath,
  organizationDiscussionThreadsPath,
  organizationHelpPath,
  organizationsPath,
  sessionPath,
  settingsProfilePath,
  signInPath,
} from "@/routes";


interface SharedProps {
  auth?: {
    session?: {
      id: string;
    };
    user?: {
      id: number;
      name: string;
      email: string;
      avatar?: string;
      profile_complete?: boolean;
    };
    organization?: {
      id: number;
      name: string;
    };
  };
  notifications?: {
    unread_count?: number;
  };
}


const page = usePage<SharedProps>();

useFlash();
useAnnouncementToasts();
useUserNotificationToasts();

const { isOnline } = useNetworkStatus();
const isRefreshing = ref(false);
const showWelcomeGuide = ref(false);

const handleRefresh = () => {
  if (isRefreshing.value) return;
  router.reload({
    onStart: () => { isRefreshing.value = true; },
    onFinish: () => { isRefreshing.value = false; },
  });
};

const session = computed(() => page.props.auth?.session);
const user = computed(() => page.props.auth?.user);
const organization = computed(() => page.props.auth?.organization);
const getInitials = useInitials();

const globalNotices = computed<GlobalNotice[]>(() => {
  const notices: GlobalNotice[] = [];

  if (user.value && user.value.profile_complete === false) {
    notices.push({
      id: "profile-completion",
      priority: 100,
      variant: "warning",
      title: "Completá tu perfil",
      description: "Para aprovechar Condor al máximo, completá tu nombre, cargo y bio.",
      dismissible: false,
      cta: {
        label: "Ir a ajustes de perfil",
        href: settingsProfilePath(),
      },
    });
  }

  if (!isOnline.value) {
    notices.push({
      id: "offline-state",
      priority: 50,
      variant: "warning",
      title: "Sin conexión",
      description: "Estás sin conexión.",
      dismissible: false,
    });
  }

  return notices;
});

const syncWelcomeGuideVisibility = () => {
  showWelcomeGuide.value = shouldShowWelcomeGuide({
    component: page.component,
    userId: user.value?.id,
  });
};

const dismissWelcomeGuide = () => {
  markWelcomeGuideDismissed(user.value?.id);
  showWelcomeGuide.value = false;
};
// Diagnostic: detect when auth props disappear mid-session (ghost logout)
// This should never happen during normal SPA navigation — if it fires,
// it means a stale SW cache or failed Inertia response wiped shared props.
watch(
  () => page.props.auth,
  (newAuth, oldAuth) => {
    const hadUser = !!(oldAuth as SharedProps["auth"])?.user;
    const hasUser = !!(newAuth as SharedProps["auth"])?.user;
    if (hadUser && !hasUser) {
      console.warn(
        "[auth-ghost] Auth props lost during SPA navigation.",
        {
          url: page.url,
          component: page.component,
          previousAuth: oldAuth,
          newAuth,
          onLine: navigator.onLine,
          swController: !!navigator.serviceWorker?.controller,
        },
      );
    }
  },
  { deep: true },
);

watch(
  [() => page.component, () => user.value?.id],
  () => {
    syncWelcomeGuideVisibility();
  },
);

watch(showWelcomeGuide, (visible) => {
  if (typeof document === "undefined") return;
  document.body.classList.toggle("overflow-hidden", visible);
});

// Clear app badge when the app gains focus (PWA Badging API)
const clearBadge = () => {
  void (navigator as Navigator & { clearAppBadge?: () => Promise<void> }).clearAppBadge?.();
};
// Catch network errors from Inertia visits (offline / connection lost)
// instead of letting them bubble as uncaught AxiosErrors.
// Returning false prevents Inertia's default error modal.
const removeExceptionHook = router.on("exception", (event) => {
  const error = (event as unknown as { detail?: { exception?: Error } }).detail?.exception;
  const isNetworkError =
    !navigator.onLine ||
    (error instanceof Error && (error.message === "Network Error" || error.name === "AxiosError"));

  if (isNetworkError) {
    event.preventDefault();
    progress.remove();
    toast.warning("Error de red — intenta de nuevo");
    return false;
  }
});

onMounted(() => {
  clearBadge();
  window.addEventListener("focus", clearBadge);
  syncWelcomeGuideVisibility();
});
onBeforeUnmount(() => {
  window.removeEventListener("focus", clearBadge);
  removeExceptionHook();
  document.body.classList.remove("overflow-hidden");
});

const currentPath = computed(() => {
  const normalizedPath = page.url.replace(/\/$/, "");
  return normalizedPath.length > 0 ? normalizedPath : "/";
});


const handleLogout = () => {
  if (session.value) {
    router.delete(sessionPath({ id: session.value.id }));
  }
};



const navItems = computed(() => {
  if (!organization.value) return [];
  return [
    {
      title: "Hilos",
      href: organizationDiscussionThreadsPath(organization.value.id),
      icon: MessageSquareIcon,
    },
    {
      title: "Directorio",
      href: organizationDirectoryPath(organization.value.id),
      icon: BookUserIcon,
    },
    {
      title: "Grupos",
      href: organizationDiscussionGroupsPath(organization.value.id),
      icon: UsersIcon,
    },
    {
      title: "Ajustes",
      href: settingsProfilePath(),
      icon: SettingsIcon,
    },
  ];
});

const mobileNavItems = computed(() => {
  if (!organization.value || !user.value) return [];

  return [
    {
      id: "hilos",
      title: "Hilos",
      href: organizationDiscussionThreadsPath(organization.value.id),
      icon: MessageSquareIcon,
    },
    {
      id: "directorio",
      title: "Directorio",
      href: organizationDirectoryPath(organization.value.id),
      icon: BookUserIcon,
    },
    {
      id: "settings",
      title: "Ajustes",
      href: settingsProfilePath(),
      icon: SettingsIcon,
    },
  ] as const;
});

const isPathActive = (href: string) => {
  const normalizedHref = href.replace(/\/$/, "") || "/";

  if (normalizedHref === "/") {
    return currentPath.value === "/";
  }

  return (
    currentPath.value === normalizedHref ||
    currentPath.value.startsWith(`${normalizedHref}/`)
  );
};

</script>

<template>
  <div class="mx-auto flex h-dvh max-w-7xl flex-col overflow-hidden">
    <!-- Navbar -->
    <div
      class="sticky top-0 z-40 px-3 pt-[calc(env(safe-area-inset-top)+0.5rem)]"
      style="view-transition-name: shell-header"
    >
      <nav
        class="mx-auto max-w-7xl rounded-2xl border border-border bg-background px-4 sm:px-6"
      >
        <div class="flex h-14 items-center justify-between gap-4">

          <!-- Center-left: logo + desktop nav links -->
          <div class="flex flex-1 items-center lg:items-stretch lg:justify-start">
            <!-- Logo -->
            <Link
              :href="organization ? organizationDiscussionThreadsPath(organization.id) : organizationsPath()"
              class="flex shrink-0 items-center gap-2"
              view-transition
            >
              <img :src="'/icon.png'" alt="Condor" class="size-10" />
              <span v-if="organization" class="hidden text-sm font-semibold lg:block">{{ organization.name }}</span>
            </Link>

            <!-- Desktop nav links -->
            <div class="hidden lg:ml-8 lg:flex lg:items-center lg:space-x-1">
              <Link
                v-for="item in navItems"
                :key="item.title"
                :href="item.href"
                view-transition
                class="inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition-colors"
                :class="
                  isPathActive(item.href)
                    ? 'bg-foreground/[0.05] text-foreground'
                    : 'text-muted-foreground hover:bg-foreground/[0.03] hover:text-foreground'
                "
              >
                <component :is="item.icon" class="size-4" />
                {{ item.title }}
              </Link>
            </div>
          </div>

          <!-- Right: notifications, user dropdown -->
          <div class="flex items-center gap-x-2">
            <Link
              v-if="organization"
              :href="organizationHelpPath(organization.id)"
              class="hidden items-center rounded-lg px-3 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-foreground/[0.03] hover:text-foreground lg:inline-flex"
              aria-label="Abrir guía de ayuda"
            >
              Ayuda
            </Link>
            <button
              class="relative inline-flex size-8 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring lg:hidden"
              aria-label="Actualizar"
              @click="handleRefresh"
            >
              <RefreshCwIcon class="size-4" :class="{ 'animate-spin': isRefreshing }" />
            </button>
            <NotificationsDropdown />

            <!-- User dropdown -->
            <DropdownMenu v-if="user">
              <DropdownMenuTrigger as-child>
                <button class="relative flex rounded-full text-sm focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring">
                  <span class="sr-only">Abrir menú de usuario</span>
                  <Avatar class="size-8">
                    <AvatarFallback class="bg-neutral-200 text-xs text-black dark:bg-neutral-700 dark:text-white">
                      {{ getInitials(user.name) }}
                    </AvatarFallback>
                  </Avatar>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" class="w-48">
                <div class="flex flex-col space-y-1 p-2">
                  <p class="text-sm font-medium">{{ user.name }}</p>
                  <p class="text-xs text-muted-foreground">{{ user.email }}</p>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem as-child>
                  <Link :href="settingsProfilePath()" class="w-full cursor-pointer">
                    Ajustes
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem class="cursor-pointer" @click="handleLogout">
                  Cerrar sesión
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            <Link
              v-if="!user"
              :href="signInPath()"
              class="rounded-md border px-2.5 py-1.5 text-xs font-medium"
            >
              Log in
            </Link>
          </div>
        </div>
      </nav>

      <GlobalNoticeRail :notices="globalNotices" />
    </div>
    <!-- Main content -->
    <main
      class="relative flex-1 min-h-0 overflow-y-auto lg:flex lg:min-h-0 lg:flex-col lg:overflow-hidden"
      style="view-transition-name: main-content"
    >
      <slot />
    </main>

    <!-- Mobile bottom nav -->
    <nav
      v-if="mobileNavItems.length > 0"
      class="shrink-0 px-4 pb-[calc(env(safe-area-inset-bottom)+0.5rem)] pt-0 lg:hidden"
      style="view-transition-name: shell-bottom-nav"
    >
      <div class="mx-auto flex max-w-sm items-stretch justify-around rounded-2xl border border-border bg-background px-2 py-1">
        <Link
          v-for="item in mobileNavItems"
          :key="item.id"
          :href="item.href"
          view-transition
          class="relative flex flex-1 flex-col items-center justify-center gap-0.5 rounded-xl px-2.5 py-2 transition-colors hover:bg-muted/50 active:scale-95"
          :class="
            isPathActive(item.href)
              ? 'bg-muted/70 text-primary'
              : 'text-muted-foreground'
          "
          @click="haptic.light()"
        >
          <span
            v-if="isPathActive(item.href)"
            class="absolute -top-1 h-0.5 w-8 rounded-full bg-primary"
          />
          <component :is="item.icon" class="size-5" />
          <span class="text-[10px] font-medium">{{ item.title }}</span>
        </Link>
      </div>
    </nav>

    <Toaster rich-colors position="top-center" />
    <PwaUpdateDialog />
    <SplashScreen />
    <div
      v-if="showWelcomeGuide"
      class="fixed inset-0 z-50 overflow-y-auto bg-background/95 backdrop-blur-sm"
    >
      <WelcomeGuide
        :organization-name="organization?.name || 'Condor'"
        @dismiss="dismissWelcomeGuide"
      />
    </div>
  </div>
</template>
